<script setup>
import { computed, ref, watch } from 'vue'
import { Button, FormField, Input, Modal, SegmentedControl, Select, Textarea, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { saveModelProviderCapability } from '../../../api/model-capabilities.js'
import { MODEL_CAPABILITIES } from '../model-capability.js'

const props = defineProps({
  open: { type: Boolean, required: true },
  catalog: { type: Object, required: true },
  provider: { type: Object, required: true },
  providerCapability: { type: Object, default: null },
})
const emit = defineEmits(['update:open', 'saved'])
const { t } = useI18n()
const toast = useToast()
const saving = ref(false)
const form = ref(emptyCapabilityForm())

const capabilityOptions = computed(() => MODEL_CAPABILITIES.map(value => ({
  value,
  label: t(`visualGameAdmin.llm.capabilities.types.${value}`),
})))
const adapterOptions = computed(() => props.catalog.adapters[form.value.capability]
  .map(value => ({ value, label: value })))
const credentialOptions = computed(() => props.catalog.adapter_credential_sources[form.value.capability][form.value.adapter]
  .map(value => ({ value, label: t(`visualGameAdmin.llm.providers.credentials.${value}`) })))
const statusOptions = computed(() => [
  { value: 'enabled', label: t('visualGameAdmin.llm.providers.status.enabled') },
  { value: 'disabled', label: t('visualGameAdmin.llm.providers.status.disabled') },
])
const status = computed({
  get: () => form.value.enabled ? 'enabled' : 'disabled',
  set: value => { form.value.enabled = value === 'enabled' },
})
const paramsError = computed(() => {
  if (form.value.params.trim() === '') return ''
  try {
    const value = JSON.parse(form.value.params)
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? ''
      : t('visualGameAdmin.llm.providers.errors.notObject')
  } catch {
    return t('visualGameAdmin.llm.providers.errors.invalidJson')
  }
})
const invalid = computed(() => (
  form.value.code.trim() === ''
  || form.value.adapter === ''
  || form.value.credential_source === ''
  || paramsError.value !== ''
))

watch(() => props.open, open => {
  if (!open) return
  form.value = props.providerCapability === null
    ? emptyCapabilityForm()
    : {
        id: Number(props.providerCapability.id),
        provider_id: Number(props.providerCapability.provider_id),
        code: props.providerCapability.code,
        capability: props.providerCapability.capability,
        adapter: props.providerCapability.adapter,
        credential_source: props.providerCapability.credential_source,
        base_url: props.providerCapability.base_url ?? '',
        params: props.providerCapability.params === null
          ? ''
          : JSON.stringify(props.providerCapability.params, null, 2),
        enabled: props.providerCapability.enabled === 1,
      }
})

watch(() => form.value.capability, capability => {
  if (props.providerCapability !== null) return
  form.value.adapter = props.catalog.adapters[capability][0]
})

watch(() => form.value.adapter, adapter => {
  if (adapter === '') return
  form.value.credential_source = props.catalog.adapter_credential_sources[form.value.capability][adapter][0]
})

function emptyCapabilityForm() {
  const capability = 'text'
  const adapter = props.catalog.adapters[capability][0]
  return {
    id: null,
    provider_id: Number(props.provider.id),
    code: '',
    capability,
    adapter,
    credential_source: props.catalog.adapter_credential_sources[capability][adapter][0],
    base_url: '',
    params: '',
    enabled: true,
  }
}

async function submit() {
  if (invalid.value) return
  saving.value = true
  try {
    const catalog = await saveModelProviderCapability({
      id: form.value.id,
      provider_id: form.value.provider_id,
      code: form.value.code.trim(),
      capability: form.value.capability,
      adapter: form.value.adapter,
      credential_source: form.value.credential_source,
      base_url: form.value.base_url.trim() === '' ? null : form.value.base_url.trim(),
      params: form.value.params.trim() === '' ? null : JSON.parse(form.value.params),
      enabled: form.value.enabled,
    })
    emit('saved', catalog)
    emit('update:open', false)
    toast.success(t('visualGameAdmin.llm.providers.toast.saved'))
  } catch (error) {
    toast.error(error.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Modal
    :open="open"
    :title="t(`visualGameAdmin.llm.providers.capabilityForm.${providerCapability ? 'editTitle' : 'createTitle'}`)"
    width="720px"
    @update:open="emit('update:open', $event)"
  >
    <form class="provider-capability-form" @submit.prevent="submit">
      <FormField :label="t('visualGameAdmin.llm.providers.capabilityForm.code')" required>
        <Input v-model="form.code" :disabled="providerCapability !== null" />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.providers.capabilityForm.capability')" required>
        <Select v-model="form.capability" :options="capabilityOptions" :disabled="providerCapability !== null" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.providers.basic.adapter')" required>
        <Select v-model="form.adapter" :options="adapterOptions" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.providers.basic.credentialSource')" required>
        <Select v-model="form.credential_source" :options="credentialOptions" block />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.providers.basic.baseUrl')">
        <Input v-model="form.base_url" type="url" />
      </FormField>
      <FormField :label="t('visualGameAdmin.llm.providers.basic.enabled')">
        <SegmentedControl v-model="status" :options="statusOptions" />
      </FormField>
      <FormField
        class="provider-capability-form__wide"
        :label="t('visualGameAdmin.llm.providers.basic.params')"
        :error="paramsError"
      >
        <Textarea v-model="form.params" :rows="5" :invalid="paramsError !== ''" />
      </FormField>
    </form>
    <template #footer>
      <Button variant="ghost" @click="emit('update:open', false)">{{ t('visualGameAdmin.common.cancel') }}</Button>
      <Button variant="primary" :loading="saving" :disabled="invalid" @click="submit">
        {{ t('visualGameAdmin.common.save') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.provider-capability-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--drama-space-lg);

  &__wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 760px) {
  .provider-capability-form {
    grid-template-columns: 1fr;
  }
}
</style>
